import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import userAdminService from "../../services/userAdminService";
import { Button, Badge, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import ConfirmModal from "../Common/ConfirmModal";
import InfoPill from "../Common/InfoPill";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";

const UsersList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Only show admin and super_admin entries; default to "all" to show both
  const [userType, setUserType] = useState("all");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const toTitleCase = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const load = async () => {
    try {
      setLoading(true);
      // Pass both admin and super_admin types, or the selected single type
      const filterType = userType === "all" ? "admin,super_admin" : userType;
      const res = await userAdminService.list({ user_type: filterType });
      const usersWithFullName = (res.users || []).map((u) => ({
        ...u,
        name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
      }));
      setRows(usersWithFullName);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const requestDelete = (user) => {
    setUserToDelete(user);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await userAdminService.remove(userToDelete.id);
      toast.success("User deleted");
      setRows((prev) => prev.filter((u) => u.id !== userToDelete.id));
    } catch (e) {
      toast.error(e.response.data.message || "Delete failed");
    } finally {
      setShowConfirmModal(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setUserToDelete(null);
  };

  const headers = [
    {
      prop: "name",
      title: "User",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              overflow: "hidden",
              background: "#f1f1f1",
            }}
          >
            {row.image_url && (
              <img
                src={row?.image_url || "/not-img-found.png"} // ✅ fallback if missing
                alt="avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.currentTarget.onerror = null; // prevent infinite loop
                  e.currentTarget.src = "/not-img-found.png"; // ✅ fallback if load fails
                }}
              />
            )}
          </div>
          <Link
            to={`/admin/users/details/${row.id}`}
            className="text-decoration-none fw-bold text-primary"
            style={{ cursor: "pointer" }}
          >
            {row.first_name || ""} {row.last_name || ""}
          </Link>
        </div>
      ),
    },
    {
      prop: "email",
      title: "Email",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "phone",
      title: "Phone",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "user_type",
      title: "Type",
      isSortable: true,
      isFilterable: true,
      cell: (row) => <Badge bg="secondary">{toTitleCase(row.user_type)}</Badge>,
    },
    {
      prop: "created_at",
      title: "Created",
      isSortable: true,
      isFilterable: true,
      cell: (row) =>
        (() => {
          const { formatDateToDDMMYYYY } = require("../../utils/dateUtils");
          return row.created_at ? formatDateToDDMMYYYY(row.created_at) : "-";
        })(),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-warning"
            // onClick={() => navigate(`/admin/users/${row.id}/edit`)}
            onClick={() => navigate(`/admin/users/${row.id}/edit`)}
            disabled={loading}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => requestDelete(row)}
            disabled={loading}
          >
            <FaTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Users
        </Link>
      </div>

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${userToDelete?.first_name || ""
          } ${userToDelete?.last_name || ""}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={loading}
      />

      <DatatableWrapper
        body={rows}
        headers={headers}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="d-flex align-items-left  mb-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search users..."
                className="w-100 btn-success"
              />
            </div>

            {/* User Type Filter */}
            <div style={{ maxWidth: "250px" }}>
              <Form.Select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="all">All Admins</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </Form.Select>
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill left="Total Users" right={rows?.length || 0} />
          </div>

          <div className="d-flex align-items-center">
            <Button
              onClick={() => navigate("/admin/users/new")}
              variant="success"
              className="btn-sm btn-success"
              disabled={loading}
            >
              New User
            </Button>
          </div>
        </div>

        {/* Table */}
        <div
          className="table-responsive shadow-sm"
          style={{ position: "relative" }}
        >
          {loading && rows.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "4px",
              }}
            >
              <Spinner animation="border" variant="success" />
            </div>
          )}
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default UsersList;
